/**
 * UsdtWithdrawStatus — статус он-чейн вывода USDT (Flow step="usdt_withdraw_status").
 * Опрашивает сервер, пока отправка идёт (pending/sending), показывает сумму, адрес, комиссию
 * и хеши транзакций. Пропсы: withdrawal (стартовое состояние), onDone.
 */
import { useEffect, useState } from "react";
import { useMainButton } from "@/telegram/useMainButton";
import { Screen, Card, Row } from "@/components/Screen";
import { Spinner } from "@/components/Spinner";
import { useCopy, CopyIcon } from "@/components/copy";
import { usdtWithdrawApi, type CustodialWithdrawal } from "@/lib/api";
import { formatUsdt, maskMiddle } from "@/lib/format";

type Props = { withdrawal: CustodialWithdrawal; onDone: () => void };

const LOOK: Record<string, { label: string; tone: string; icon: string }> = {
  pending: { label: "В обработке", tone: "text-amber-300", icon: "⏳" },
  sending: { label: "Отправляем в сеть", tone: "text-amber-300", icon: "🔄" },
  completed: { label: "Отправлено", tone: "text-emerald-300", icon: "✅" },
  failed: { label: "Ошибка", tone: "text-red-300", icon: "⚠️" },
};

export function UsdtWithdrawStatus({ withdrawal, onDone }: Props) {
  const [wd, setWd] = useState<CustodialWithdrawal>(withdrawal);
  const live = wd.status === "pending" || wd.status === "sending";

  useEffect(() => {
    if (!live) return;
    let alive = true;
    const id = setInterval(async () => {
      try {
        const next = await usdtWithdrawApi.get(wd.id);
        if (alive) setWd(next);
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [live, wd.id]);

  useMainButton({ text: "Готово", onClick: onDone, visible: !live });

  const look = LOOK[wd.status] ?? LOOK.pending;
  const net = "TRC20";

  return (
    <Screen title="Вывод USDT">
      <div className="space-y-4">
        <div className="rounded-2xl border border-white/[0.06] bg-surface-card p-6 text-center">
          <div className="text-4xl">{live ? <Spinner className="mx-auto h-9 w-9" /> : look.icon}</div>
          <div className={`mt-3 text-[17px] font-semibold ${look.tone}`}>{look.label}</div>
          <div className="mt-1 text-[26px] font-bold tabular-nums">{formatUsdt(wd.amount_usdt)}</div>
          <div className="mt-0.5 text-[12px] text-white/40">на {maskMiddle(wd.to_address, 8, 8)}</div>
        </div>

        <Card>
          <Row label="Сеть" value={net} />
          <Row label="Комиссия" value={`${wd.fee_usdt} USDT`} />
          <Row label="Списано всего" value={formatUsdt(wd.amount_usdt + wd.fee_usdt)} />
          {wd.tx_hash && <TxRow label="Перевод" hash={wd.tx_hash} />}
          {wd.fee_tx_hash && <TxRow label="Комиссия (tx)" hash={wd.fee_tx_hash} />}
        </Card>

        {wd.status === "failed" && wd.error && (
          <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-[13px] text-red-300">
            {wd.error}
          </div>
        )}
      </div>
    </Screen>
  );
}

function TxRow({ label, hash }: { label: string; hash: string }) {
  const { copied, copy } = useCopy(hash);
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-[14px] text-white/45">{label}</span>
      <button onClick={copy} className="flex items-center gap-1.5 font-mono text-[13px] text-white/80 active:opacity-70">
        {maskMiddle(hash, 6, 6)}
        <CopyIcon className="h-3.5 w-3.5" />
        {copied && <span className="text-[11px] text-brand">✓</span>}
      </button>
    </div>
  );
}
